import { axiosInstance } from "../../utils/axios";
import { BACCHUS_API_URL } from "./constants";
import {
  ExportQuote,
  ExportQuoteListResponse,
  ExportQuoteUpdateBody,
  ExportQuoteWriteBody,
  SegmentFilterBy,
  SingleExportQuoteResponse,
} from "./types";

export interface FetchExportQuotesParams {
  page?: number;
  pageSize?: number;
  filterBy?: SegmentFilterBy;
  /** Search quotes by name (case and accent insensitive) */
  search?: string;
  /** AbortController signal for request cancellation */
  signal?: AbortSignal;
}

/**
 * Fetch export quotes with pagination, filtering, and search
 */
export async function fetchExportQuotes({
  page = 1,
  pageSize = 50,
  filterBy = "my",
  search,
  signal,
}: FetchExportQuotesParams = {}): Promise<ExportQuoteListResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
    filter_by: filterBy,
  });

  if (search) {
    params.set("search", search);
  }

  const { data } = await axiosInstance.get<ExportQuoteListResponse>(
    `${BACCHUS_API_URL}/export-quotes?${params.toString()}`,
    { signal }
  );

  return data;
}

/**
 * Get single export quote by ID
 */
export async function getExportQuote(id: string): Promise<ExportQuote> {
  const { data } = await axiosInstance.get<SingleExportQuoteResponse>(
    `${BACCHUS_API_URL}/export-quotes/${id}`
  );

  return data.data;
}

/**
 * Create new export quote
 */
export async function createExportQuote(
  body: ExportQuoteWriteBody
): Promise<ExportQuote> {
  const { data } = await axiosInstance.post<SingleExportQuoteResponse>(
    `${BACCHUS_API_URL}/export-quotes`,
    body
  );

  return data.data;
}

/**
 * Update existing export quote
 */
export async function updateExportQuote(
  id: string,
  body: ExportQuoteUpdateBody
): Promise<ExportQuote> {
  const { data } = await axiosInstance.patch<SingleExportQuoteResponse>(
    `${BACCHUS_API_URL}/export-quotes/${id}`,
    body
  );

  return data.data;
}

/**
 * Delete export quote by ID
 */
export async function deleteExportQuote(id: string): Promise<void> {
  await axiosInstance.delete(`${BACCHUS_API_URL}/export-quotes/${id}`);
}
